import React, { useState } from 'react';
import { Check, X, ChevronDown, ChevronRight, BookUser, Calendar, Clock, StickyNote, GraduationCap, MapPin } from 'lucide-react';
import type { FilingProposal } from '@/services/api/agentApi';
import { Button } from '@/components/ui/button';

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const KIND_META: Record<FilingProposal['kind'], { icon: React.ReactNode; label: string; color: string }> = {
  contact:               { icon: <BookUser className="h-3 w-3" />,      label: 'Contact',         color: 'bg-blue-500/10 text-blue-600' },
  recurring_schedule:    { icon: <Calendar className="h-3 w-3" />,      label: 'Schedule',        color: 'bg-purple-500/10 text-purple-600' },
  one_off_event:         { icon: <Clock className="h-3 w-3" />,         label: 'Event',           color: 'bg-cyan-500/10 text-cyan-600' },
  note_worthy:           { icon: <StickyNote className="h-3 w-3" />,    label: 'Note',            color: 'bg-yellow-500/10 text-yellow-600' },
  expertise_domain:      { icon: <GraduationCap className="h-3 w-3" />, label: 'Expertise',       color: 'bg-indigo-500/10 text-indigo-600' },
  expertise_location:    { icon: <MapPin className="h-3 w-3" />,        label: 'Location',        color: 'bg-emerald-500/10 text-emerald-600' },
};

interface Props {
  filing: FilingProposal;
  onApprove?: (id: string) => void | Promise<void>;
  onReject?: (id: string) => void | Promise<void>;
}

export const FilingCard: React.FC<Props> = ({ filing, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const meta = KIND_META[filing.kind];

  const handle = async (action: () => Promise<void> | void) => {
    setBusy(true);
    try { await action(); } finally { setBusy(false); }
  };

  return (
    <div className="border rounded-lg p-4 mb-3 bg-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded font-medium inline-flex items-center gap-1 ${meta.color}`}>
              {meta.icon}{meta.label}
            </span>
            <span className="text-xs text-muted-foreground">{(filing.llm_confidence * 100).toFixed(0)}%</span>
            <span className="text-xs text-muted-foreground ml-auto">{timeAgo(filing.decided_at)}</span>
          </div>
          <p className="text-sm font-medium leading-tight mb-2">{renderTitle(filing)}</p>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            details
          </button>
          {expanded && (
            <pre className="mt-2 text-xs bg-muted/30 p-2 rounded overflow-x-auto">
              {JSON.stringify(filing.entity_payload, null, 2)}
            </pre>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {filing.status === 'pending_review' && onApprove && (
            <Button size="sm" disabled={busy} onClick={() => handle(() => onApprove(filing.filing_id))}>
              <Check className="h-3 w-3 mr-1" /> File
            </Button>
          )}
          {filing.status === 'pending_review' && onReject && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => handle(() => onReject(filing.filing_id))}>
              <X className="h-3 w-3 mr-1" /> Dismiss
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

function renderTitle(filing: FilingProposal): string {
  const p = filing.entity_payload as Record<string, unknown>;
  switch (filing.kind) {
    case 'contact': return (p.contact_name as string) || 'Contact';
    case 'recurring_schedule': return (p.title as string) || 'Recurring schedule';
    case 'one_off_event': return (p.title as string) || 'Event';
    case 'note_worthy': return (p.title as string) || 'Note';
    case 'expertise_domain': return (p.domain as string) || 'Expertise domain';
    case 'expertise_location': return (p.city as string) || (p.region as string) || 'Location';
  }
}
